import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/layout/PageHeader';
import { ContentWrapper } from '@/components/layout/ContentWrapper';
import {
  Activity, CheckCircle, AlertCircle, Clock, XCircle, RefreshCw,
  FileText, Download, Search, Filter, Eye,
} from 'lucide-react';
import type { ProcessingJob } from '@/types/api.types';
import { useAuth } from '@/hooks/useAuth';

const mockJobs: ProcessingJob[] = [
  {
    id: '1', batchId: 'BATCH-2025-001', fileName: 'realpage_claims_january_2025.xlsx',
    jobType: 'RealPage', status: 'processing', progress: 67, recordCount: 2347,
    processedCount: 1572, startTime: '2025-01-26T10:30:00Z',
    estimatedTimeRemaining: 180, creditsUsed: 1572,
  },
  {
    id: '2', batchId: 'BATCH-2025-002', fileName: 'client_database_backup_q4.csv',
    jobType: 'RealPage', status: 'completed', progress: 100, recordCount: 1850,
    processedCount: 1850, qualifiedCount: 247, startTime: '2025-01-26T08:15:00Z',
    completedTime: '2025-01-26T08:47:00Z', creditsUsed: 1850, qualificationRate: 13.4,
  },
  {
    id: '3', batchId: 'BATCH-2025-003', fileName: 'pfas_exposure_claims.xlsx',
    jobType: 'PFAS', status: 'completed', progress: 100, recordCount: 756,
    processedCount: 756, qualifiedCount: 89, startTime: '2025-01-25T16:20:00Z',
    completedTime: '2025-01-25T16:35:00Z', creditsUsed: 756, qualificationRate: 11.8,
  },
  {
    id: '4', batchId: 'BATCH-2025-004', fileName: 'legacy_client_data.csv',
    jobType: 'General', status: 'failed', progress: 23, recordCount: 3200,
    processedCount: 736, startTime: '2025-01-25T14:10:00Z',
    errorMessage: 'Invalid file format detected in row 737. Expected date format MM/DD/YYYY.',
    creditsUsed: 0,
  },
  {
    id: '5', batchId: 'BATCH-2025-005', fileName: 'new_client_intake_jan.xlsx',
    jobType: 'RealPage', status: 'queued', progress: 0, recordCount: 1200,
    processedCount: 0, startTime: '2025-01-26T11:00:00Z',
  },
  {
    id: '6', batchId: 'BATCH-2025-006', fileName: 'flynn_greathouse_full_docket.xlsx',
    jobType: 'Overlap' as any, status: 'processing', progress: 52, recordCount: 1890,
    processedCount: 980, startTime: '2025-01-26T12:00:00Z',
    estimatedTimeRemaining: 240, creditsUsed: 980,
    tortSubStatuses: [
      { tortCode: 'RLP', status: 'COMPLETE', processedRows: 1890, matchCount: 312 },
      { tortCode: 'PFAS', status: 'PROCESSING', processedRows: 980, matchCount: 47 },
      { tortCode: 'VGA', status: 'QUEUED', processedRows: 0, matchCount: 0 },
      { tortCode: 'SMA', status: 'QUEUED', processedRows: 0, matchCount: 0 },
    ],
  },
];

const statusConfig = {
  queued: { color: 'bg-gray-100 text-gray-800', icon: Clock, label: 'Queued' },
  processing: { color: 'bg-blue-100 text-blue-800', icon: Activity, label: 'Processing' },
  completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Completed' },
  failed: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Failed' },
  cancelled: { color: 'bg-gray-100 text-gray-600', icon: XCircle, label: 'Cancelled' },
};
const ProcessingJobs: React.FC = () => {
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [liveJobs, setLiveJobs] = useState<ProcessingJob[]>([]);
  const { token } = useAuth();

  const fetchLiveJobs = useCallback(async () => {
    if (!token) return;
    const stored = JSON.parse(localStorage.getItem('claimsiq_jobs') || '[]');
    if (stored.length === 0) return;
    const baseUrl = process.env.REACT_APP_API_URL || 'https://claims-iq.com/api';
    const updated: ProcessingJob[] = [];
    for (const job of stored) {
      try {
        const res = await fetch(baseUrl + '/upload/status/' + job.processingId, {
          headers: { Authorization: 'Bearer ' + token },
        });
        if (res.ok) {
          const data = await res.json();
          updated.push({
            id: job.processingId,
            batchId: job.processingId,
            fileName: job.fileName,
            jobType: 'Overlap' as any,
            status: data.status === 'completed' ? 'completed' : data.status === 'error' ? 'failed' : 'processing',
            progress: data.status === 'completed' ? 100 : 50,
            recordCount: data.recordCount || data.size || 0,
            processedCount: data.processedCount || 0,
            qualifiedCount: data.qualifiedCount || 0,
            startTime: new Date(data.uploadTime || Date.now()).toISOString(),
            completedTime: data.status === 'completed' ? new Date().toISOString() : undefined,
            creditsUsed: data.processedCount || 0,
          });
        } else {
          updated.push({
            id: job.processingId, batchId: job.processingId, fileName: job.fileName,
            jobType: 'Overlap' as any, status: 'processing', progress: 50,
            recordCount: 0, processedCount: 0, startTime: job.startTime, creditsUsed: 0,
          });
        }
      } catch {
        updated.push({
          id: job.processingId, batchId: job.processingId, fileName: job.fileName,
          jobType: 'Overlap' as any, status: 'processing', progress: 0,
          recordCount: 0, processedCount: 0, startTime: job.startTime, creditsUsed: 0,
        });
      }
    }
    setLiveJobs(updated);
  }, [token]);

  useEffect(() => {
    fetchLiveJobs();
    const interval = setInterval(fetchLiveJobs, 5000);
    return () => clearInterval(interval);
  }, [fetchLiveJobs]);

  const allJobs = [...liveJobs, ...mockJobs];

  const filteredJobs = allJobs.filter((job) => {
    const matchesSearch = job.fileName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.batchId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || job.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const summaryStats = {
    total: allJobs.length,
    processing: allJobs.filter((j) => j.status === 'processing').length,
    completed: allJobs.filter((j) => j.status === 'completed').length,
    failed: allJobs.filter((j) => j.status === 'failed').length,
  };

  const formatTime = (iso: string) => {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  };

  return (
    <ContentWrapper>
      <PageHeader
        title="Processing Jobs"
        subtitle="Track and manage your claims discovery processing jobs"
        breadcrumbs={[
          { label: 'Claims Discovery' },
          { label: 'Processing Jobs', isActive: true },
        ]}
        actions={
          <Button className="bg-blue-600 hover:bg-blue-700 text-white">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        }
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Jobs', value: summaryStats.total, icon: FileText, bg: 'bg-gray-100', iconColor: 'text-gray-600' },
          { label: 'Processing', value: summaryStats.processing, icon: Activity, bg: 'bg-blue-100', iconColor: 'text-blue-600' },
          { label: 'Completed', value: summaryStats.completed, icon: CheckCircle, bg: 'bg-green-100', iconColor: 'text-green-600' },
          { label: 'Failed', value: summaryStats.failed, icon: XCircle, bg: 'bg-red-100', iconColor: 'text-red-600' },
        ].map((stat, i) => {
          const Icon = stat.icon;
          return (
            <Card key={i} className="bg-white border border-gray-200 shadow-sm">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`p-2 rounded-lg ${stat.bg}`}>
                  <Icon className={`h-5 w-5 ${stat.iconColor}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  <p className="text-xs text-gray-500">{stat.label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card className="bg-white border border-gray-200 shadow-sm mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by file name or batch ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="all">All Statuses</option>
              <option value="queued">Queued</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Job List */}
      <div className="space-y-4">
        {filteredJobs.map((job) => {
          const statusCfg = statusConfig[job.status];
          const StatusIcon = statusCfg.icon;

          return (
            <Card key={job.id} className="bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  {/* Job Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-medium text-gray-900 truncate">{job.fileName}</span>
                      <Badge className={statusCfg.color}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {statusCfg.label}
                      </Badge>
                      <Badge variant="outline" className="text-xs">{job.jobType}</Badge>
                    </div>
                    {/* Per-tort sub-status for Overlap jobs */}
                    {job.jobType === 'Overlap' && (job as any).tortSubStatuses && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {(job as any).tortSubStatuses.map((sub: any) => {
                          const subColors: Record<string, string> = {
                            COMPLETE: 'bg-green-100 text-green-700',
                            PROCESSING: 'bg-blue-100 text-blue-700 animate-pulse',
                            QUEUED: 'bg-gray-100 text-gray-500',
                            FAILED: 'bg-red-100 text-red-700',
                          };
                          const icon = sub.status === 'COMPLETE' ? '\u2713' : sub.status === 'PROCESSING' ? '\u2026' : sub.status === 'FAILED' ? '\u2717' : '\u25CB';
                          return (
                            <span key={sub.tortCode} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${subColors[sub.status] || 'bg-gray-100 text-gray-500'}`}>
                              {icon} {sub.tortCode}: {sub.status === 'COMPLETE' ? `${sub.matchCount} matches` : sub.status.charAt(0) + sub.status.slice(1).toLowerCase()}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>{job.batchId}</span>
                      <span>Started: {formatTime(job.startTime)}</span>
                      {job.completedTime && <span>Completed: {formatTime(job.completedTime)}</span>}
                      <span>{job.recordCount.toLocaleString()} records</span>
                    </div>
                  </div>

                  {/* Progress / Stats */}
                  <div className="flex items-center gap-4">
                    {job.status === 'processing' && (
                      <div className="w-48">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-gray-500">{job.processedCount.toLocaleString()} / {job.recordCount.toLocaleString()}</span>
                          <span className="font-medium text-blue-600">{job.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${job.progress}%` }}
                          />
                        </div>
                        {job.estimatedTimeRemaining && (
                          <p className="text-xs text-gray-400 mt-1">
                            ~{Math.ceil(job.estimatedTimeRemaining / 60)} min remaining
                          </p>
                        )}
                      </div>
                    )}

                    {job.status === 'completed' && (
                      <div className="text-right">
                        <p className="text-sm font-semibold text-green-600">{job.qualifiedCount} qualified</p>
                        <p className="text-xs text-gray-500">{job.qualificationRate}% rate</p>
                      </div>
                    )}

                    {job.status === 'failed' && (
                      <div className="max-w-xs">
                        <p className="text-xs text-red-600">{job.errorMessage}</p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {job.status === 'completed' && (
                        <>
                          <Button variant="outline" size="sm">
                            <Eye className="h-4 w-4 mr-1" /> View
                          </Button>
                          <Button variant="outline" size="sm">
                            <Download className="h-4 w-4 mr-1" /> Export
                          </Button>
                        </>
                      )}
                      {job.status === 'failed' && (
                        <Button variant="outline" size="sm">
                          <RefreshCw className="h-4 w-4 mr-1" /> Retry
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </ContentWrapper>
  );
};

export default ProcessingJobs;